import React, { useEffect, useState } from "react";
import styles from "./UploadPhoto.module.scss";
import classNames from "classnames/bind";
import { CloseIcon } from "~/assets/icons";
import BaseInputV2 from "~/components/common/base-input-v2/BaseInputV2";
import BaseTextarea from "~/components/common/base-textarea/BaseTextarea";
import BaseUploadFileV2 from "~/components/common/base-upload-file-v2/BaseUploadFileV2";
const cx = classNames.bind(styles);

interface UploadFileModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (isCreate: boolean, data: any) => void;
  photo?: any;
}

const emptyForm = {
  fileSize: "",
  fileName: "",
  description: "",
  file: null as File | null,
  files: [] as File[],
  fileType: "",
  title: "",
};

const UploadPhotoModal: React.FC<UploadFileModalProps> = ({
  isOpen,
  onCancel,
  onSave,
  photo,
}) => {
  const [formData, setFormData] = useState({ ...emptyForm });
  const [error, setError] = useState({ file: "" });

  const [mode, setMode] = useState<"CREATE" | "EDIT">("CREATE");
  const [imgSrc, setImgSrc] = useState("");

  const handleUpload = () => {
    let hasError = false;
    let newError = { file: "" };
    const hasFiles =
      mode === "EDIT"
        ? true
        : formData.files.length > 0 || !!formData.file;

    if (!hasFiles && mode === "CREATE") {
      newError.file = "Please select at least one file.";
      hasError = true;
    }

    // FB-36: title and description are optional; only the photo itself is required.
    setError(newError);

    if (!hasError) {
      onSave(mode === "CREATE", formData);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({ ...emptyForm });
      setImgSrc("");
      setMode("CREATE");
      setError({ file: "" });
    }
  }, [isOpen]);

  useEffect(() => {
    if (photo) {
      setMode("EDIT");
      setFormData({
        ...emptyForm,
        description: photo.description || "",
        title: photo.title || "",
      });
      setImgSrc(photo.url || "");
    } else {
      setMode("CREATE");
      setImgSrc("");
    }
  }, [photo]);

  if (!isOpen) return <></>;

  return (
    <div className={cx("overlay")} onClick={onCancel}>
      <div
        className={cx("upload-file-modal-container")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cx("header")}>
          <h4 className={cx("header-title")}>
            {mode === "CREATE" ? "Upload" : "Edit"} Photo
            {mode === "CREATE" && formData.files.length > 1
              ? `s (${formData.files.length})`
              : ""}
          </h4>
          <div
            className="flex items-center justify-end w-[30px] h-[30px]"
            onClick={onCancel}
          >
            <CloseIcon color="#ffffff" fill="#ffffff" />
          </div>
        </div>

        <div className={cx("content")}>
          <BaseUploadFileV2
            multiple={mode === "CREATE"}
            onChange={(data: any) => {
              const files: File[] = data.files?.length
                ? data.files
                : data.file
                  ? [data.file]
                  : [];
              setFormData({
                ...formData,
                file: files[0] ?? null,
                files,
                fileName: data.fileName || "",
                fileSize: data.fileSize || "",
                fileType: data.fileType || "",
              });
              setError({ ...error, file: "" });
            }}
            errorMessage={error.file}
            url={imgSrc}
          />

          <BaseInputV2
            type="textarea"
            label="Image Title"
            placeholder=""
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
          />

          <BaseTextarea
            type="textarea"
            label="Description"
            placeholder=""
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </div>

        <div className={cx("footer")}>
          <button className={cx("footer-cancel-button")} onClick={onCancel}>
            Cancel
          </button>
          <button className={cx("footer-save-button")} onClick={handleUpload}>
            {mode === "CREATE" ? "Upload" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadPhotoModal;
